import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  QrCode,
  Newspaper,
  Megaphone,
  CalendarDays,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import '../styles/landing.css';
import aluLogo from '@assets/LABOR CONNECT LOGO WHITE.png';
import aluLogoPhone from '@assets/LABOR CONNECT LOGO WHITE PHONE.png';

const FEATURES = [
  {
    icon: QrCode,
    title: 'Digital ID',
    description: 'Access your secure QR-enabled union ID for fast identity verification and member services.',
  },
  {
    icon: Newspaper,
    title: 'Events',
    description: 'View upcoming union events, announcements, and updates directly from your member dashboard.',
  },
  {
    icon: Megaphone,
    title: 'Request and Assistance',
    description: 'Submit concerns, request support, and track assistance requests in one place.',
  },
  {
    icon: CalendarDays,
    title: 'Venue Bookings',
    description: 'Reserve union venues, check availability, and manage your booking requests in one place.',
  },
];

export default function LandingPage({ onGetStarted, onLogin }) {
  const hideScrollbarTimeoutRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('landing-scrollbar');

    const showScrollbar = () => {
      root.classList.add('landing-scrollbar--active');
      if (hideScrollbarTimeoutRef.current) {
        window.clearTimeout(hideScrollbarTimeoutRef.current);
      }
      hideScrollbarTimeoutRef.current = window.setTimeout(() => {
        root.classList.remove('landing-scrollbar--active');
      }, 900);
    };

    window.addEventListener('mousemove', showScrollbar, { passive: true });

    return () => {
      if (hideScrollbarTimeoutRef.current) {
        window.clearTimeout(hideScrollbarTimeoutRef.current);
      }
      window.removeEventListener('mousemove', showScrollbar);
      root.classList.remove('landing-scrollbar--active');
      root.classList.remove('landing-scrollbar');
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.15,
      }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, []);

  return (
    <div className="landing-page">
      <main className="landing-page__main">
        <section className="landing-page__hero">
          <div className="landing-page__hero-top">
            <div className="landing-page__brand">
              <button
                type="button"
                className="landing-page__brand-button"
                onClick={() => window.location.reload()}
                aria-label="Reload page"
              >
                <div className="landing-page__brand-icon">
                  <picture>
                    <source media="(max-width: 768px)" srcSet={aluLogoPhone} />
                    <img src={aluLogo} alt="Associated Philippine Seafarers Union" />
                  </picture>
                </div>
              </button>
            </div>
            <div className="landing-page__hero-top-actions">
              <button type="button" className="landing-page__hero-top-btn" onClick={onGetStarted}>
                Join Now
              </button>
              <button type="button" className="landing-page__hero-top-btn" onClick={onLogin}>
                Sign In
              </button>
            </div>
          </div>
          <div className="landing-page__hero-content">
            <div className="landing-page__badges">
              <span>Mobile Optimized</span>
              <span>Desktop Ready</span>
            </div>
            <h2>
              <span className="landing-page__headline-block">Smarter Union Access</span>
              <span className="landing-page__headline-block landing-page__headline-block--accent">Starts Here</span>
            </h2>
            <p>
              <span className="landing-page__copy-block landing-page__copy-block--1">
                Access your Digital ID, read union news, submit requests for assistance, and manage your venue bookings
              </span>
              <span className="landing-page__copy-block landing-page__copy-block--2">
                through our comprehensive digital platform designed to support every
              </span>
              <span className="landing-page__copy-block landing-page__copy-block--3">LaborConnect member.</span>
            </p>
            <div className="landing-page__actions">
              <button type="button" className="button button--primary" onClick={onLogin}>
                Sign In
                <ArrowRight size={18} />
              </button>
              <button type="button" className="button button--secondary" onClick={onGetStarted}>
                Create Account
              </button>
            </div>
            <button
              type="button"
              className="landing-page__scroll-down"
              onClick={() => {
                const featuresSection = document.querySelector('.landing-page__features');
                if (featuresSection) {
                  const headerOffset = 50;
                  const elementPosition = featuresSection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
              aria-label="Scroll down to features"
            >
              <ChevronDown size={32} />
            </button>
          </div>
        </section>

        <section className="landing-page__features">
          <div className="landing-page__features-header animate-on-scroll">
            <h3>
              Everything you need as a <span className="landing-page__text-accent">union member</span>
            </h3>
            <p>
              From digital ID cards to benefit tracking, LaborConnect delivers modern tools that keep every member supported
              and connected.
            </p>
          </div>
          <div className="landing-page__feature-grid">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className="landing-page__feature animate-on-scroll"
                  style={{ transitionDelay: `${i * 0.15}s` }}
                >
                  <div className="landing-page__feature-icon">
                    <Icon size={28} />
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="landing-page__cta">
          <div className="landing-page__cta-inner animate-on-scroll">
            <h3>Faster Access for Every Member</h3>
            <div className="landing-page__cta-side">
              <p>
                Manage your union essentials faster with LaborConnect, anytime and anywhere.
              </p>
              <div className="landing-page__cta-actions">
                <button type="button" className="landing-page__cta-primary" onClick={onGetStarted}>
                  Get Started Today
                  <ArrowRight size={18} />
                </button>
                <button type="button" className="landing-page__cta-secondary" onClick={onLogin}>
                  Already a member?
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-page__footer">
        <div className="landing-page__footer-inner animate-on-scroll">
          <div className="landing-page__footer-brand">
            <img src={aluLogo} alt="LaborConnect" className="landing-page__footer-logo" />
          </div>
          <p>
            Empowering union members through digital innovation and comprehensive membership services.
          </p>
          <div className="landing-page__footer-meta">
            <span>Member Benefits</span>
            <span>Support Center</span>
            <span>Contact Us</span>
            <Link to="/privacy-policy">Data Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
          </div>
          <div className="landing-page__footer-note">
            © 2026 LaborConnect. Labor Unions Membership Portal - Luzon Regional. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

LandingPage.propTypes = {
  onGetStarted: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
};
